import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Copy, Landmark, MapPin, ReceiptText, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * PixInfoCard — Bloco de transparência com dados fiscais/bancários da RAF Direct
 * exibido em telas de pagamento (Stripe processa; este card só informa quem é o
 * beneficiário legal). Puxa dados de /api/public/company-info (ENV-driven).
 * Props: `compact?: boolean` → versão enxuta (sem título grande).
 */
const PixInfoCard = ({ compact = false }) => {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get("/public/company-info").then(({ data }) => setInfo(data)).catch(() => setInfo(null));
  }, []);
  if (!info) return null;

  const copy = (label, value) => {
    if (!value) return;
    navigator.clipboard?.writeText(value);
    toast.success(`${label} copiado`);
  };

  const Row = ({ label, value, testid, mono = true }) => (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-slate-100 last:border-b-0">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold whitespace-nowrap">{label}</div>
      <div className="flex items-center gap-1.5 min-w-0 flex-1 justify-end">
        <span className={`text-xs text-slate-900 ${mono ? "font-mono" : "font-semibold text-right"} truncate`} data-testid={`${testid}-value`}>
          {value || "—"}
        </span>
        {value && (
          <button type="button" onClick={() => copy(label, value)}
                  data-testid={`${testid}-copy`}
                  className="p-1 rounded hover:bg-slate-100 text-slate-500 flex-shrink-0"
                  title={`Copiar ${label}`}>
            <Copy className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );

  const enderecoLinha = [info.endereco, info.bairro].filter(Boolean).join(" · ");
  const cidadeLinha = [info.cidade, info.uf].filter(Boolean).join("/") + (info.cep ? ` · CEP ${info.cep}` : "");

  return (
    <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 shadow-sm"
         data-testid="pix-info-card">
      {!compact && (
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-5 h-5 text-emerald-700" />
          <h3 className="font-bold text-[#0B2B62] text-lg">Beneficiário oficial</h3>
        </div>
      )}
      <p className="text-xs text-slate-600 mb-3 leading-relaxed">
        Todos os pagamentos são processados pelo <b>Stripe</b> (cartão{info.pix_enabled ? " · PIX" : ""}).
        {info.pix_enabled && " Ao escolher PIX no checkout, o QR-Code e a chave copia-e-cola são gerados automaticamente com os dados oficiais abaixo."}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
        <div>
          <div className="flex items-center gap-2 text-slate-700 mt-1 mb-1">
            <ReceiptText className="w-4 h-4" />
            <span className="text-xs uppercase font-bold tracking-wider">Dados fiscais</span>
          </div>
          <Row label="Razão Social" value={info.razao_social} testid="pix-razao" mono={false} />
          <Row label="CNPJ" value={info.cnpj} testid="pix-cnpj" />
          {(info.endereco || info.cidade) && (
            <>
              <div className="flex items-center gap-2 text-slate-700 mt-3 mb-1">
                <MapPin className="w-4 h-4" />
                <span className="text-xs uppercase font-bold tracking-wider">Endereço</span>
              </div>
              <div className="py-1.5 text-xs text-slate-800 leading-snug" data-testid="pix-endereco-value">
                {enderecoLinha && <div>{enderecoLinha}</div>}
                {cidadeLinha && <div className="text-slate-600">{cidadeLinha}</div>}
              </div>
            </>
          )}
        </div>
        <div>
          <div className="flex items-center gap-2 text-slate-700 mt-1 mb-1">
            <Landmark className="w-4 h-4" />
            <span className="text-xs uppercase font-bold tracking-wider">Chave PIX / Banco</span>
          </div>
          <Row label={`Chave PIX${info.pix_tipo ? ` (${info.pix_tipo})` : ""}`} value={info.pix_chave} testid="pix-chave" />
          {info.banco && info.banco !== "Consulte no boleto/Stripe" && <Row label="Banco" value={info.banco} testid="pix-banco" mono={false} />}
          {info.agencia && info.agencia !== "0000" && <Row label="Agência" value={info.agencia} testid="pix-agencia" />}
          {info.conta && info.conta !== "00000-0" && <Row label="Conta" value={info.conta} testid="pix-conta" />}
        </div>
      </div>
    </div>
  );
};

export default PixInfoCard;
export { PixInfoCard };
